import { useState } from "react";
import CopyIcon from "../../../../../assets/svg/copy-icon";
import { EscalationContactCardProps } from "../../../utils/types";
import { Tooltip } from "../../../../../components/Tooltip/Tooltip";

const utcTimeToLocal12h = (time: string): string => {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  const utcDate = new Date();
  utcDate.setUTCHours(hours, minutes, 0, 0);
  const localHours = utcDate.getHours();
  const localMinutes = utcDate.getMinutes();
  const period = localHours >= 12 ? "PM" : "AM";
  const displayHours = localHours % 12 || 12;
  return `${String(displayHours).padStart(2, "0")}:${String(localMinutes).padStart(2, "0")} ${period}`;
};

export const EscalationContactCard = ({
  data,
  level,
}: EscalationContactCardProps) => {
  const { position, email, meeting_link, name, phone, profile_picture, availability } =
    data;

  const [copiedKey, setCopiedKey] = useState<string>("");
  const [tooltipOpen, setTooltipOpen] = useState<string>("");
  const [hoverKey, setHoverKey] = useState<string>("");

  const handleCopy = (value: string, key: string) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTooltipOpen(key);
    setTimeout(() => setCopiedKey(""), 2000);
  };

  const assistanceData = [
    {
      label: "Email",
      value: email,
      canCopy: true,
    },
    {
      label: "Mobile Number",
      value: phone,
      canCopy: true,
    },
    {
      label: "Available",
      value:
        availability?.start_time && availability?.end_time
          ? (
              <>
                {`${utcTimeToLocal12h(availability.start_time)} - ${utcTimeToLocal12h(availability.end_time)} `}
                <span className="text-gray-placeholder dark:text-gray-placeholder-dk">(Local Time)</span>
              </>
            )
          : "-",
      canCopy: false,
    },
  ];

  return (
    <div className="group relative mt-3 w-max border border-border-gray dark:border-border-gray-dk rounded-lg p-4 bg-pure-white dark:bg-pure-white-dk z-10 hover:border-selected-option-highlight dark:hover:border-selected-option-highlight-dk hover:bg-selected-option-highlight dark:hover:bg-selected-option-highlight-dk transition-all duration-300">
      <span className="absolute -top-3 left-3 z-20 text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk rounded px-2 py-[2px] group-hover:bg-verba-blue dark:group-hover:bg-verba-blue-dk group-hover:text-fixed-white dark:group-hover:text-fixed-white-dk transition-all duration-300">
        Level {level}
      </span>
      <div className="flex items-center gap-2.5">
        <img
          src={profile_picture}
          alt={name}
          className="w-[50px] h-[50px] rounded-full object-cover shrink-0"
        />
        <div className="w-full flex justify-between items-center">
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-medium text-verba-indigo dark:text-verba-indigo-dk">
              {name}
            </span>
            <span className="text-xs text-verba-chat dark:text-verba-chat-dk">
              {position}
            </span>
          </div>
          <button
            onClick={() => window.open(meeting_link, "_blank")}
            className="hidden group-hover:block min-w-[127px] py-[3px] rounded-md text-md font-semibold text-pure-white dark:text-pure-white-dk bg-verba-indigo dark:bg-verba-indigo-dk border border-border-gray dark:border-border-gray-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk transition-all duration-300"
          >
            Schedule Call
          </button>
        </div>
      </div>
      <div className="mt-4 flex items-center">
        {assistanceData?.map((item, index) => [
          <div key={item?.label} className="flex items-start gap-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-gray-275 dark:text-gray-275-dk">
                {item?.label}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk break-all">
                  {item?.value}
                </span>
                {item?.canCopy && (
                  <Tooltip
                    content={
                      copiedKey === item?.label && hoverKey !== item?.label
                        ? "Copied!"
                        : "Click to copy"
                    }
                    open={tooltipOpen === item?.label}
                    onOpenChange={(open) =>
                      setTooltipOpen(open ? item?.label : "")
                    }
                  >
                    <button
                      onClick={() => handleCopy(item.value as string, item.label)}
                      onMouseEnter={() => setHoverKey(item.label)}
                      onMouseLeave={() => setHoverKey("")}
                    >
                      <CopyIcon className="w-3 h-3 text-gray-placeholder dark:text-gray-placeholder-dk" />
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>,
          index < assistanceData?.length - 1 ? (
            <div
              key={`divider-${index}`}
              className="w-px h-5 bg-border-gray dark:bg-border-gray-dk mx-4"
            />
          ) : null,
        ])}
      </div>
    </div>
  );
};
